import type { QuickNote, TaskTemplate } from "../../domain/entities.ts";
import type { Root, SavedView } from "../../domain/app-types.ts";
import { randomUuid } from "../../infrastructure/crypto/random.ts";
import type { PortableArchive, PortableSpace } from "./transfer.ts";
import type { ProgressReporter } from "../../ui/operation-progress.ts";

export interface RestoreArchiveCapabilities {
  request(path: string, init?: RequestInit): Promise<Response>;
  apiError(response: Response, fallback: string): Promise<string>;
  addRoot(root: Root): void;
  importSpace(space: PortableSpace, rootId: string, views: SavedView[], progress?: ProgressReporter): Promise<void>;
  importTemplates(templates: TaskTemplate[]): Promise<void>;
  importNotes(notes: QuickNote[]): Promise<void>;
  setActiveRoot(rootId: string): Promise<void>;
  progress?: ProgressReporter;
}

export async function restorePortableArchive(archive: PortableArchive, capabilities: RestoreArchiveCapabilities): Promise<void> {
  let activeRoot: string | null = null;
  let restored = 0;
  for (const space of archive.spaces) {
    const id = randomUuid();
    capabilities.progress?.({ stage: "Creating restored spaces", completed: restored, total: archive.spaces.length });
    const response = await capabilities.request("/api/v1/roots", { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ id, name: `${space.name} (restored)`.slice(0, 120), color: space.color || "indigo" }) });
    if (!response.ok) throw new Error(await capabilities.apiError(response, `Could not restore “${space.name}”`));
    capabilities.addRoot(await response.json());
    await capabilities.importSpace(space, id, archive.savedViews as SavedView[], capabilities.progress);
    capabilities.progress?.({ stage: "Restored spaces", completed: ++restored, total: archive.spaces.length });
    activeRoot = id;
  }
  capabilities.progress?.({ stage: "Saving personal templates and private notes" });
  await capabilities.importTemplates(archive.personalTemplates);
  await capabilities.importNotes(archive.quickNotes);
  if (activeRoot) await capabilities.setActiveRoot(activeRoot);
}
